'use client';

import React, {
  ChangeEvent,
  cloneElement,
  createContext,
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import cn from '@/helpers/utils/classNames';

import { ButtonProps } from './Button';
import { RadioGroupProps } from './Radio';
import { SelectProps } from './Select';
import { TextInputProps } from './TextInput';

type FormData = Record<string, string | number | readonly string[] | undefined>;

type FieldVariants = {
  outline?: 'default' | 'primary';
};

type FormProps = {
  children: ReactNode;
  className?: string;
  onSubmit?: (data: Record<string, string>, resetData?: () => void) => void;
  onSubmitFailed?: (data: Record<string, string>) => void;
  reCalcRefs?: boolean;
  variant?: FieldVariants;
};

type FormFieldRef = { validate: () => boolean };

type FormFieldsRefs = Record<string, FormFieldRef>;

type FormContextProps = {
  data: FormData;
  onChange: (fieldName: string, value: string | number | readonly string[] | undefined) => void;
  submitForm: () => void;
  refs: FormFieldsRefs;
  addRef: (fieldName: string, ref: FormFieldRef) => void;
  variant?: FieldVariants;
};

export type CustomFormData = FormData;

const context = createContext<FormContextProps>({} as FormContextProps);

const FormContextProvider = context.Provider;
const useFormContext = () => useContext(context);

const isTextInput = (b: unknown): b is TextInputProps => {
  return ((b as TextInputProps) || {}).variant !== undefined;
};

const isRadioGroup = (b: unknown): b is RadioGroupProps => {
  return (b as RadioGroupProps)?.initialSelectedValue !== undefined;
};

const isButtonField = (b: unknown): b is ButtonProps => {
  return ((b as ButtonProps) || {}).scheme !== undefined;
};

const isSubmitButtonField = (b: unknown): b is ButtonProps => {
  return ((b as ButtonProps) || {}).type === 'submit';
};

const isFormComponent = (b: unknown): b is FormProps => {
  return (b as FormProps)?.onSubmit !== undefined;
};

const isSelectField = (b: unknown): b is SelectProps<any> => {
  return (b as SelectProps<any>)?.options !== undefined;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const SoloFormFieldComponent = ({ child }: { child: ReactElement }) => {
  const fieldRef = useRef<FormFieldRef>();
  const { data, refs, onChange, submitForm, addRef, variant } = useFormContext();

  if (!child) return child;

  if (isFormComponent(child.props))
    return cloneElement(child, {
      ...child.props,
    });

  if (isSubmitButtonField(child.props))
    return cloneElement(child, {
      ...child.props,

      onClick: (event: Event) => {
        event.preventDefault();
        submitForm();
        child.props.onClick && child.props.onClick(event);
      },
    });

  if (isRadioGroup(child.props)) {
    return cloneElement(child, {
      ...child.props,
      ...(child.props.name
        ? {
            onMount: () => {
              if (!child.props.name) return;
              if (typeof data[child.props.name] === 'undefined')
                return onChange(child.props.name, child.props.initialSelectedValue || '');
            },
            initialSelectedValue: data[child.props.name] || child.props.initialSelectedValue,
            onChange: (value: string) => {
              onChange(child.props.name, value);
              child.props.onChange && child.props.onChange(value);
            },
          }
        : {}),
    });
  }

  if (isSelectField(child.props)) {
    return cloneElement(child, {
      ...child.props,
      ...(child.props.name
        ? {
            ref: child.props.ref || fieldRef,
            outline: child.props.outline || variant?.outline,
            onMount: () => {
              if (!child.props.name) return;

              if (typeof data[child.props.name] === 'undefined') onChange(child.props.name, child.props.value || '');
              if (typeof refs[child.props.name] === 'undefined')
                addRef(child.props.name, child.props.ref || fieldRef.current);
            },
            value: data[child.props.name] || child.props.value,
            onChange: (value: any) => {
              onChange(child.props.name, value);
              child.props.onChange && child.props.onChange(value);
            },
          }
        : {}),
    });
  }

  if (isTextInput(child.props) && !isButtonField(child.props)) {
    return cloneElement(child, {
      ...child.props,
      ...(child.props.name || child.props.id
        ? {
            ref: child.props.ref || fieldRef,
            outline: child.props.outline || variant?.outline,
            onMount: () => {
              if (!child.props.name) return;
              if (typeof data[child.props.name] === 'undefined') onChange(child.props.name, child.props.value || '');
              if (typeof refs[child.props.name] === 'undefined')
                addRef(child.props.name, child.props.ref || fieldRef.current);
            },
            value: data[child.props.name || child.props.id || ''] || '',
            onChange: (event: ChangeEvent<HTMLInputElement>) => {
              onChange(child.props.name || child.props.id, event.target.value);
              child.props.onChange && child.props.onChange(event);
            },
          }
        : {}),
    });
  }

  if (child.props?.children)
    return cloneElement(child, {
      ...child.props,
      children: <FormField>{child?.props.children}</FormField>,
    });

  return child;
};

const FormFieldComponent: React.FC<{ children: ReactElement | ReactElement[] }> = ({ children }) => {
  if (!children) return <></>;

  return (
    <>
      {React.Children.map(children, child => (
        <SoloFormField child={child} />
      ))}
    </>
  );
};

const FormComponent: React.FC<FormProps> = ({
  children,
  className,
  onSubmitFailed,
  reCalcRefs,
  variant,
  onSubmit,
  ...restProps
}) => {
  const [data, setData] = useState<FormData>({} as FormData);
  const [refs, setRefs] = useState<FormFieldsRefs>({} as FormFieldsRefs);
  const [reCalcRefsInternal, setReCalcRefs] = useState<number>(0);
  const onChange = (fieldName: string, value: string | number | readonly string[] | undefined) => {
    setData((prevState: FormData) => ({
      ...prevState,
      [fieldName]: value,
    }));
  };

  const addRef = (fieldName: string, ref: FormFieldRef) => {
    if (refs[fieldName]) return;
    setRefs(prev => ({ ...prev, [fieldName]: ref }));
  };

  const resetData = () => setData(data => Object.keys(data).reduce((acc, val) => ({ ...acc, [val]: null }), {}));

  const submitForm = async () => {
    const isValidationFailed = await Promise.all(
      Object.values(refs)
        .filter(ref => !!ref?.validate)
        .map(async ref => await ref.validate())
    );

    if (isValidationFailed.includes(true)) return onSubmitFailed && onSubmitFailed(data as Record<string, string>);

    onSubmit && onSubmit(data as Record<string, string>, resetData);
  };

  useEffect(() => {
    if (reCalcRefs) setReCalcRefs(0);
  }, [reCalcRefs]);

  useEffect(() => {
    function reCalc() {
      if (!reCalcRefs || Object.keys(refs).length === 0 || reCalcRefsInternal > 1) return;
      setRefs({} as FormFieldsRefs);
    }

    reCalc();
  }, [reCalcRefs, reCalcRefsInternal, refs]);

  useEffect(() => {
    function reCalc() {
      if (Object.keys(refs).length > 0) return;
      setReCalcRefs(prev => prev + 1);
    }

    reCalc();
  }, [refs]);

  return (
    <div className={cn('form-component', className)} {...restProps}>
      <FormContextProvider value={{ data, onChange, submitForm, refs, addRef, variant }}>
        <FormField>{children as ReactElement}</FormField>
      </FormContextProvider>
    </div>
  );
};

const FormField = React.memo(FormFieldComponent);
const Form = React.memo(FormComponent);
const SoloFormField = React.memo(SoloFormFieldComponent);

export default Form;
